import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Card,
  Checkbox,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { UploadParticipantsButton } from './UploadParticipantsButton';
import { useAppSearchParams } from 'src/hooks/useAppSearchParams';
import { useGetUsersQuery } from 'src/api/contests/getUsers';
import { useState } from 'react';
import { UserType } from 'src/types/domain.types';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import { TabPanel } from 'src/components/language-tabs/TabPanel';
import { UserListHead } from 'src/sections/@dashboard/user';
import { useGetContestQuery } from 'src/api/contests/getContest';
import { GameTypeEnum } from 'src/enums/contest.enum';
import {
  applySortFilter,
  FUTURE_USERS_TABLE_HEAD,
  getComparator,
  TABLE_HEAD,
} from './helpers';
import { MakeDeadAliveMenuItem } from './helpers/MakeDeadAliveMenuItem';

export const ParticipantsSection = () => {
  const navigate = useNavigate();
  const { contestId } = useAppSearchParams();

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<UserType[]>([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName] = useState('');
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [openId, setOpenId] = useState<number>(-1);
  const [isUserAlive, setIsUserAlive] = useState<boolean>(false);
  const [userTab, setUserTab] = useState<'current' | 'future'>('current');

  const {
    data: usersData,
    isLoading: isGetUsersLoading,
    isError: isGetUsersError,
    error: getGameUsersError,
  } = useGetUsersQuery({
    contestId,
  });
  const { data: game } = useGetContestQuery(contestId);

  const users = usersData?.contest_users || [];
  const futureUsers = usersData?.future_users || [];

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
    setOpenId(Number(event.currentTarget.id));
    setIsUserAlive(
      users.find((user) => user.user_id === Number(event.currentTarget.id))
        ?.is_alive ?? false
    );
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(-1);
  };

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(users);
      return;
    }
    setSelected([]);
  };

  const handleClick = (
    _event: React.MouseEvent<unknown> | React.ChangeEvent<HTMLInputElement>,
    user: UserType
  ) => {
    const selectedIndex = selected.indexOf(user);
    let newSelected: UserType[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, user);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangeUsersTab = (_event: any, newValue: any) => {
    setUserTab(newValue);
  };

  const filteredUsers = applySortFilter(
    users,
    getComparator(order, orderBy),
    filterName
  );

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Participants </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Participants
          </Typography>

          <Stack direction='row' spacing={2}>
            {game?.game_type === GameTypeEnum.PRIVATE && (
              <UploadParticipantsButton />
            )}
          </Stack>
        </Stack>

        {futureUsers.length > 0 && (
          <Tabs
            value={userTab}
            onChange={handleChangeUsersTab}
            defaultValue='current'
            component='div'
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label='Current Users' value='current' />
            <Tab label='Future Users' value='future' />
          </Tabs>
        )}

        <Card>
          {/* <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
          /> */}

          {isGetUsersLoading ? (
            <Skeleton height={200} />
          ) : isGetUsersError ? (
            <Alert severity='error' sx={{ m: 2 }}>
              Error loading users: {getGameUsersError?.message}
            </Alert>
          ) : (
            <>
              <TabPanel value={userTab} index='current'>
                <Scrollbar>
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                      <UserListHead
                        order={order}
                        orderBy={orderBy}
                        headLabel={TABLE_HEAD}
                        rowCount={users.length}
                        numSelected={selected.length}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAllClick}
                      />
                      <TableBody>
                        {filteredUsers.map((user, index) => {
                          const selectedUser = selected.some(
                            (selectedUser) =>
                              selectedUser.user_id === user.user_id
                          );

                          return (
                            <TableRow
                              key={index}
                              sx={{
                                '&:last-child td, &:last-child th': {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell padding='checkbox'>
                                <Checkbox
                                  checked={selectedUser}
                                  onChange={(event) => handleClick(event, user)}
                                />
                              </TableCell>
                              <TableCell component='th' scope='row'>
                                {user.user_id}
                              </TableCell>
                              <TableCell component='th' scope='row'>
                                {user.sso_user_id}
                              </TableCell>
                              <TableCell component='th' scope='row'>
                                {user.username}
                              </TableCell>
                              <TableCell component='th' scope='row'>
                                {user.is_alive ? (
                                  <Iconify
                                    icon={'eva:checkmark-circle-2-outline'}
                                    sx={{ color: 'green' }}
                                  />
                                ) : (
                                  <Iconify
                                    icon={'eva:close-circle-outline'}
                                    sx={{ color: 'red' }}
                                  />
                                )}
                              </TableCell>
                              <TableCell component='th' scope='row'>
                                {user.is_back_in ? (
                                  <Iconify
                                    icon={'eva:checkmark-circle-2-outline'}
                                    sx={{ color: 'green' }}
                                  />
                                ) : (
                                  <Iconify
                                    icon={'eva:close-circle-outline'}
                                    sx={{ color: 'red' }}
                                  />
                                )}
                              </TableCell>

                              <TableCell align='right'>
                                <IconButton
                                  size='large'
                                  color='inherit'
                                  onClick={handleOpenMenu}
                                  id={String(user.user_id)}
                                >
                                  <Iconify icon={'eva:more-vertical-fill'} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>

                      {isNotFound && (
                        <TableBody>
                          <TableRow>
                            <TableCell
                              align='center'
                              colSpan={6}
                              sx={{ py: 3 }}
                            >
                              <Paper
                                sx={{
                                  textAlign: 'center',
                                }}
                              >
                                <Typography variant='h6' paragraph>
                                  Not found
                                </Typography>

                                <Typography variant='body2'>
                                  No results found for &nbsp;
                                  <strong>&quot;{filterName}&quot;</strong>.
                                  <br /> Try checking for typos or using
                                  complete words.
                                </Typography>
                              </Paper>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Scrollbar>
                {/* <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component='div'
                  count={users.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                /> */}
              </TabPanel>

              {/* Future users */}
              <TabPanel value={userTab} index='future'>
                <Scrollbar>
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                      <UserListHead
                        order={order}
                        orderBy={orderBy}
                        headLabel={FUTURE_USERS_TABLE_HEAD}
                      />
                      <TableBody>
                        {futureUsers.map(({ sso_user_id }) => {
                          return (
                            <TableRow
                              key={sso_user_id}
                              sx={{
                                '&:last-child td, &:last-child th': {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell component='th' scope='row'>
                                {sso_user_id}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>

                      {isNotFound && (
                        <TableBody>
                          <TableRow>
                            <TableCell
                              align='center'
                              colSpan={6}
                              sx={{ py: 3 }}
                            >
                              <Paper
                                sx={{
                                  textAlign: 'center',
                                }}
                              >
                                <Typography variant='h6' paragraph>
                                  Not found
                                </Typography>

                                <Typography variant='body2'>
                                  No results found for &nbsp;
                                  <strong>&quot;{filterName}&quot;</strong>.
                                  <br /> Try checking for typos or using
                                  complete words.
                                </Typography>
                              </Paper>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Scrollbar>
              </TabPanel>
            </>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() =>
            navigate(
              `/dashboard/contest-selections?contest_id=${contestId}&user_id=${openId}`
            )
          }
        >
          <Iconify icon={'eva:person-fill'} sx={{ mr: 2 }} />
          Selections
        </MenuItem>
        <MakeDeadAliveMenuItem
          openId={Number(openId)}
          isUserAlive={isUserAlive}
          setIsUserAlive={setIsUserAlive}
        />
      </Popover>
    </>
  );
};
